import React, { useMemo } from "react";
import {
  DEFAULT_CROP_RECT,
  hostFramesToSourceRect,
  isFullCropRect,
  normalizeCropRect,
} from "../../../../../components/shared/imageToolbar/imageCropModel";
import {
  beginWatermarkLogoCrop,
  getWatermarkLogoCropRect,
  resolveWatermarkCropHostFrames,
} from "../../../watermark/Editor/watermarkImageFormatAdapter";
import WatermarkLogoCropOverlay from "../../../watermark/Popup/WatermarkLogoCropOverlay";
import "../../../watermark/Popup/WatermarkPreview.css";
import "../../../watermark/Popup/WatermarkLogoCropOverlay.css";

const HANDLES = ["nw", "ne", "sw", "se", "w", "e"];
export const SIGN_PDF_UPLOAD_MARK_SIZE = { width: 320, height: 180 };

function fitContainSize(srcW, srcH, maxW, maxH) {
  const w = Math.max(1, Number(srcW) || 0);
  const h = Math.max(1, Number(srcH) || 0);
  if (!w || !h) {
    return { width: maxW, height: maxH };
  }
  const scale = Math.min(maxW / w, maxH / h, 1);
  return {
    width: Math.max(24, Math.round(w * scale)),
    height: Math.max(24, Math.round(h * scale)),
  };
}

/** Display aspect in source pixels (full image or crop window). */
function displaySourceSize(settings, cropRect) {
  const nw = Math.max(0, Number(settings?.logoNaturalWidth) || 0);
  const nh = Math.max(0, Number(settings?.logoNaturalHeight) || 0);
  if (!nw || !nh) {
    return {
      width: SIGN_PDF_UPLOAD_MARK_SIZE.width,
      height: SIGN_PDF_UPLOAD_MARK_SIZE.height,
    };
  }
  const crop = normalizeCropRect(cropRect || DEFAULT_CROP_RECT);
  if (isFullCropRect(crop)) {
    return { width: nw, height: nh };
  }
  return {
    width: Math.max(1, nw * crop.w),
    height: Math.max(1, nh * crop.h),
  };
}

function croppedImageStyle(cropRect) {
  const crop = normalizeCropRect(cropRect || DEFAULT_CROP_RECT);
  if (isFullCropRect(crop)) {
    return {
      width: "100%",
      height: "100%",
      objectFit: "fill",
    };
  }
  return {
    position: "absolute",
    width: `${100 / crop.w}%`,
    height: `${100 / crop.h}%`,
    left: `${(-crop.x / crop.w) * 100}%`,
    top: `${(-crop.y / crop.h) * 100}%`,
    maxWidth: "none",
    maxHeight: "none",
    objectFit: "fill",
  };
}

/**
 * Upload mark with watermark-style selection chrome + crop overlay.
 * Selection bounds hug the visible (contain-fit / cropped) image.
 */
const SignPdfUploadMark = ({
  settings,
  selected = false,
  disabled = false,
  onSelect = () => {},
  onChange = () => {},
}) => {
  const cropActive = Boolean(settings?.logoCropActive);
  const cropRect = getWatermarkLogoCropRect(settings);
  const frames = useMemo(
    () => resolveWatermarkCropHostFrames(settings || {}),
    [settings]
  );

  const naturalFit = useMemo(() => {
    const nw = Number(settings?.logoNaturalWidth) || 0;
    const nh = Number(settings?.logoNaturalHeight) || 0;
    return fitContainSize(
      nw || SIGN_PDF_UPLOAD_MARK_SIZE.width,
      nh || SIGN_PDF_UPLOAD_MARK_SIZE.height,
      SIGN_PDF_UPLOAD_MARK_SIZE.width,
      SIGN_PDF_UPLOAD_MARK_SIZE.height
    );
  }, [settings?.logoNaturalWidth, settings?.logoNaturalHeight]);

  const selectionFit = useMemo(() => {
    const src = displaySourceSize(settings, cropRect);
    return fitContainSize(
      src.width,
      src.height,
      SIGN_PDF_UPLOAD_MARK_SIZE.width,
      SIGN_PDF_UPLOAD_MARK_SIZE.height
    );
  }, [settings, cropRect]);

  const beginCrop = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (disabled || !settings?.logoUrl) return;
    onChange(
      beginWatermarkLogoCrop(settings, settings.logoCropAspect || "custom")
    );
    onSelect();
  };

  const finishCrop = () => {
    onChange({
      ...settings,
      logoCropActive: false,
      logoCropFrame: null,
      logoCropImageFrame: null,
      logoCropSessionStartRect: null,
      logoCropSessionStartImageFrame: null,
    });
    onSelect();
  };

  const cancelCrop = () => {
    onChange({
      ...settings,
      logoCropActive: false,
      logoCropRect: settings.logoCropSessionStartRect ?? settings.logoCropRect,
      logoCropFrame: null,
      logoCropImageFrame: null,
      logoCropSessionStartRect: null,
      logoCropSessionStartImageFrame: null,
    });
    onSelect();
  };

  if (cropActive) {
    return (
      <div
        className="spsm-upload-mark spsm-upload-mark--crop"
        style={{
          width: naturalFit.width,
          height: naturalFit.height,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <WatermarkLogoCropOverlay
          logoUrl={settings.logoUrl}
          cropFrame={frames.cropFrame}
          imageFrame={frames.imageFrame}
          cropAspect={settings.logoCropAspect || "custom"}
          naturalWidth={settings.logoNaturalWidth || 0}
          naturalHeight={settings.logoNaturalHeight || 0}
          width={naturalFit.width}
          height={naturalFit.height}
          disabled={disabled}
          cropWindowOnly
          alwaysShowActions
          onFramesChange={(cropFrame, imageFrame) => {
            onChange({
              ...settings,
              logoCropFrame: cropFrame,
              logoCropImageFrame: imageFrame,
              logoCropRect: hostFramesToSourceRect(cropFrame, imageFrame),
            });
          }}
          onDone={finishCrop}
          onCancel={cancelCrop}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`spsm-upload-mark${selected ? " is-selected" : ""}`}
      style={{
        width: selectionFit.width,
        height: selectionFit.height,
      }}
      aria-pressed={selected}
      aria-label="Signature image"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={beginCrop}
    >
      <span className="spsm-upload-mark__media">
        <img
          src={settings.logoUrl}
          alt="Signature"
          style={croppedImageStyle(cropRect)}
          draggable={false}
        />
      </span>
      {selected ? (
        <span
          className="watermark-preview__selection spsm-upload-selection"
          aria-hidden="true"
        >
          {HANDLES.map((h) => (
            <span
              key={h}
              className={`watermark-preview__handle watermark-preview__handle--${h}`}
              onPointerDown={beginCrop}
            />
          ))}
        </span>
      ) : null}
    </button>
  );
};

export function beginSignPdfUploadCrop(settings) {
  return beginWatermarkLogoCrop(settings, settings?.logoCropAspect || "custom");
}

export default SignPdfUploadMark;
